import {NS} from '../namespaces.js'

const resources = {
  en: {
    [NS.BALANCE_ROLLOUT]: {
      title: 'Balance rollout control',
      description: 'Monitor the bounded balance rollout, review validation posture, and run operator checks without leaving the admin shell.',
      actions: {
        refresh: 'Refresh',
        runHealthCheck: 'Run health check',
      },
      boolean: {
        yes: 'Yes',
        no: 'No',
      },
      policy: {
        title: 'Architecture policy',
        description: 'The balance stack now has one explicit valuation architecture. This page exists to operate the bounded cutover safely, not to revive legacy semantics.',
        alert: 'Controlled clients now treat explicit valuation as the primary money contract. Bounded rollout remains the only approved migration gate for `/v2/balance`.',
        labels: {
          primaryContract: 'Primary contract',
          runtimeBoundary: 'Runtime boundary',
          latestBoundaryMode: 'Latest boundary mode',
          cohortSize: 'Persisted cohort size',
        },
        values: {
          explicitValuationPrimary: 'explicit_valuation_primary',
          explicitCohortOnly: 'explicit_user_ids_only',
        },
      },
      health: {
        title: 'Health check',
        description: 'Run the canonical bounded-rollout health check: validation, readiness, verification, and telemetry in one verdict.',
        runError: 'Failed to run balance rollout health check.',
        fields: {
          status: 'Health status',
          continueBoundedRollout: 'Can continue bounded rollout',
          latestDecision: 'Latest decision',
          boundaryMode: 'Boundary mode',
          cohortSize: 'Cohort size',
          syntheticStatus: 'Synthetic status',
          targetUser: 'Verification target',
          reason: 'Reason',
          warnings: 'Warnings',
        },
        emptyWarnings: 'No health warnings.',
      },
      telemetry: {
        title: 'Telemetry',
        description: 'Recent bounded-rollout execution totals, alerts, and notable explicit-valuation events.',
        labels: {
          latestDecision: 'Latest decision',
          totalRequests: 'Balance requests',
          boundedRolloutHits: 'Bounded-rollout hits',
          boundedRolloutFailures: 'Bounded-rollout failures',
          degradedValuation: 'Degraded valuation requests',
          syntheticChecks: 'Synthetic checks',
          validationRuns: 'Validation runs',
        },
        alertsTitle: 'Alerts',
        emptyAlerts: 'No rollout alerts.',
        emptyEvents: 'No notable rollout events yet.',
        fields: {
          user: 'User',
        },
      },
      validation: {
        title: 'Latest validation run',
        description: 'Latest sampled balance shadow validation summary from the canonical operator path.',
        labels: {
          highSeverity: 'High severity',
          materialMismatch: 'Material mismatch',
          partialCases: 'Partial cases',
          limit: 'Sample size',
        },
        lastRunAt: 'Latest validation run: {{timestamp}}',
        empty: 'No validation run recorded yet.',
      },
      synthetic: {
        title: 'Synthetic check',
        description: 'Verify one user against the bounded-rollout gate without polluting organic traffic.',
        userIdLabel: 'User ID',
        userIdPlaceholder: 'Enter a user id',
        submit: 'Run synthetic check',
        runError: 'Failed to run synthetic check.',
        fields: {
          status: 'Verification status',
          eligible: 'Eligible for bounded rollout',
          executionMode: 'Execution mode',
          classification: 'Classification',
          severity: 'Severity',
          reason: 'Reason',
        },
        recentTitle: 'Recent synthetic checks',
        emptyRecent: 'No synthetic checks recorded yet.',
      },
      decisions: {
        title: 'Recent decisions',
        description: 'Operator decisions recorded with the validation evidence that justified them.',
        empty: 'No rollout decisions recorded yet.',
        fields: {
          cohortSizeTag: 'cohort {{count}}',
          recommendedDecision: 'Recommended',
          note: 'Note',
        },
        record: {
          title: 'Record decision',
          description: 'Persist a guarded rollout decision with current evidence. Candidate bounded rollout remains locked to the explicit proven cohort only.',
          decisionLabel: 'Decision',
          noteLabel: 'Operator note',
          notePlaceholder: 'Why is this safe right now?',
          submit: 'Record decision',
          success: 'Decision recorded.',
          error: 'Failed to record rollout decision.',
          options: {
            hold: 'Hold',
            continueShadowValidation: 'Continue shadow validation',
            candidateBoundedRollout: 'Candidate bounded rollout',
          },
        },
      },
      status: {
        loading: 'Loading balance rollout telemetry…',
        loadError: 'Failed to load balance rollout telemetry.',
      },
    },
  },
  uk: {
    [NS.BALANCE_ROLLOUT]: {
      title: 'Керування balance rollout',
      description: 'Відстежуйте bounded balance rollout, переглядайте validation posture та запускайте operator checks прямо з адмінського інтерфейсу.',
      actions: {
        refresh: 'Оновити',
        runHealthCheck: 'Запустити health check',
      },
      boolean: {
        yes: 'Так',
        no: 'Ні',
      },
      policy: {
        title: 'Політика архітектури',
        description: 'Balance stack тепер має одну explicit valuation architecture. Ця сторінка потрібна для безпечної експлуатації bounded cutover, а не для повернення legacy semantics.',
        alert: 'Контрольовані клієнти тепер читають explicit valuation як primary money contract. Bounded rollout лишається єдиним дозволеним migration gate для `/v2/balance`.',
        labels: {
          primaryContract: 'Primary contract',
          runtimeBoundary: 'Runtime boundary',
          latestBoundaryMode: 'Останній boundary mode',
          cohortSize: 'Розмір збереженого cohort',
        },
        values: {
          explicitValuationPrimary: 'explicit_valuation_primary',
          explicitCohortOnly: 'explicit_user_ids_only',
        },
      },
      health: {
        title: 'Health check',
        description: 'Запустіть canonical bounded-rollout health check: validation, readiness, verification і telemetry в одному verdict.',
        runError: 'Не вдалося виконати balance rollout health check.',
        fields: {
          status: 'Статус health check',
          continueBoundedRollout: 'Можна продовжувати bounded rollout',
          latestDecision: 'Останнє рішення',
          boundaryMode: 'Boundary mode',
          cohortSize: 'Розмір cohort',
          syntheticStatus: 'Synthetic status',
          targetUser: 'Цільовий користувач перевірки',
          reason: 'Причина',
          warnings: 'Попередження',
        },
        emptyWarnings: 'Попереджень по health check немає.',
      },
      telemetry: {
        title: 'Телеметрія',
        description: 'Останні підсумки bounded rollout, алерти та помітні події explicit valuation.',
        labels: {
          latestDecision: 'Останнє рішення',
          totalRequests: 'Balance requests',
          boundedRolloutHits: 'Bounded-rollout хіти',
          boundedRolloutFailures: 'Bounded-rollout помилки',
          degradedValuation: 'Запити з degraded valuation',
          syntheticChecks: 'Synthetic checks',
          validationRuns: 'Validation runs',
        },
        alertsTitle: 'Алерти',
        emptyAlerts: 'Алертів по rollout немає.',
        emptyEvents: 'Поки немає помітних rollout events.',
        fields: {
          user: 'Користувач',
        },
      },
      validation: {
        title: 'Останній validation run',
        description: 'Останній sampled balance shadow validation summary з canonical operator path.',
        labels: {
          highSeverity: 'High severity',
          materialMismatch: 'Матеріальні розбіжності',
          partialCases: 'Partial cases',
          limit: 'Розмір вибірки',
        },
        lastRunAt: 'Останній validation run: {{timestamp}}',
        empty: 'Validation run ще не зафіксовано.',
      },
      synthetic: {
        title: 'Synthetic check',
        description: 'Перевірте одного користувача на bounded-rollout gate без забруднення органічного трафіку.',
        userIdLabel: 'User ID',
        userIdPlaceholder: 'Введіть user id',
        submit: 'Запустити synthetic check',
        runError: 'Не вдалося виконати synthetic check.',
        fields: {
          status: 'Статус перевірки',
          eligible: 'Підходить для bounded rollout',
          executionMode: 'Execution mode',
          classification: 'Класифікація',
          severity: 'Severity',
          reason: 'Причина',
        },
        recentTitle: 'Останні synthetic checks',
        emptyRecent: 'Synthetic checks ще не записувалися.',
      },
      decisions: {
        title: 'Останні рішення',
        description: 'Операторські рішення, збережені разом із validation evidence.',
        empty: 'Рішень по rollout ще немає.',
        fields: {
          cohortSizeTag: 'cohort {{count}}',
          recommendedDecision: 'Recommended',
          note: 'Нотатка',
        },
        record: {
          title: 'Зафіксувати рішення',
          description: 'Збережіть guarded rollout decision з поточними доказами. Candidate bounded rollout лишається прив’язаним тільки до явно доведеного cohort.',
          decisionLabel: 'Рішення',
          noteLabel: 'Нотатка оператора',
          notePlaceholder: 'Чому це безпечно прямо зараз?',
          submit: 'Зберегти рішення',
          success: 'Рішення збережено.',
          error: 'Не вдалося зберегти rollout decision.',
          options: {
            hold: 'Hold',
            continueShadowValidation: 'Продовжити shadow validation',
            candidateBoundedRollout: 'Кандидат на bounded rollout',
          },
        },
      },
      status: {
        loading: 'Завантаження balance rollout telemetry…',
        loadError: 'Не вдалося завантажити balance rollout telemetry.',
      },
    },
  },
}

export default resources
